import type { CSSProperties } from 'react';
import { cx } from '@linaria/atomic';
import { ComponentProps } from '@frontend/types';
import { ButtonBar, PrimaryButton } from '../buttons';
import { Listbox, SearchableListbox, useListboxState } from '../listbox';
import type { ListboxChildren } from '../listbox/listbox.provider';
import { ModalHeader } from './atoms';
import { CenteredModal } from './molecules';
import type { ModalControlModalProps } from './provider';
import {
  selectionModalBox,
  selectionModalBoxWithHeight,
  selectionModalButtonBar,
  selectionModalHeader,
  selectionModalListbox,
  selectionModalListboxNotSearchable,
  selectionModalListboxSingleSelect,
} from './selection-modal.styles';

type ValueType = string | string[];
type SelectFn<T extends ValueType> = React.Dispatch<React.SetStateAction<T>> | ((value: T) => void);

type Props<T extends ValueType> = Pick<ModalControlModalProps, 'onClose'> & {
  children: ListboxChildren;
  onSelect: SelectFn<T>;
  searchable?: boolean;
  titleAlignment?: 'center' | 'left';
  title: string;
  trackingId?: string;
  value: T;
};

const SelectionModalContent = <T extends ValueType>({
  children,
  onClose,
  onSelect,
  searchable,
  titleAlignment = 'center',
  title,
  trackingId,
  value,
}: Props<T>) => {
  const listboxProps = useListboxState(value);
  const ListboxComponent = searchable ? SearchableListbox : Listbox;
  const multiselect = Array.isArray(value);
  const onSingleSelect = (value: string | string[]) => {
    onSelect(value as T);
    onClose();
  };
  return (
    <>
      <ModalHeader className={selectionModalHeader} id={listboxProps.labelId} textAlign={titleAlignment}>
        {title}
      </ModalHeader>
      <ListboxComponent
        {...listboxProps}
        className={cx(
          selectionModalListbox,
          !multiselect && selectionModalListboxSingleSelect,
          !searchable && selectionModalListboxNotSearchable
        )}
        onSelect={multiselect ? listboxProps.onSelect : onSingleSelect}
      >
        {children ?? null}
      </ListboxComponent>
      {multiselect && (
        <ButtonBar className={selectionModalButtonBar}>
          <PrimaryButton
            onClick={() => {
              onSelect(listboxProps.value as T);
              onClose();
            }}
            trackingId={trackingId}
          >
            Save
          </PrimaryButton>
        </ButtonBar>
      )}
    </>
  );
};

export type SelectionModalProps<T extends ValueType> = Pick<
  ComponentProps<typeof CenteredModal>,
  'disableCloseOnEscape' | 'disableCloseOnOverlayClick' | 'maxWidth' | 'minWidth' | 'mountTarget' | 'prepend' | 'show'
> &
  Props<T> & { height?: number };

const selectionModalClassname = 'wv-selection-modal';

export const SelectionModal = <T extends ValueType>({
  disableCloseOnEscape,
  disableCloseOnOverlayClick,
  minWidth = 0,
  maxWidth = 400,
  mountTarget,
  onClose,
  prepend,
  show,
  height,
  ...rest
}: SelectionModalProps<T>) => {
  return (
    <CenteredModal
      show={show}
      onClose={onClose}
      className={cx(selectionModalClassname, selectionModalBox, !!height && selectionModalBoxWithHeight)}
      minWidth={minWidth}
      maxWidth={maxWidth}
      disableCloseOnEscape={disableCloseOnEscape}
      disableCloseOnOverlayClick={disableCloseOnOverlayClick}
      mountTarget={mountTarget}
      prepend={prepend}
      flex
      style={height ? ({ '--selection-modal-height': `${height}px` } as CSSProperties) : undefined}
    >
      <SelectionModalContent onClose={onClose} {...rest} />
    </CenteredModal>
  );
};

SelectionModal.Option = Listbox.Option;
